import { Data, Layout } from "plotly.js";
import React from "react";
import Plot from "react-plotly.js";
import { defaultMaxColor, defaultMinColor } from "./grid-heatmap-editor/GridHeatmapEditor";

const groupDataInGrid = (
  data: any,
  axisStep: number = 1
) => {
  const datasetX = data[0].data;
  const datasetY = data[1].data;
  const datasetY2 = data[2]?.data || [];

  const xMin = Math.min(...datasetX.map((d: any) => d.value));
  const xMax = Math.max(...datasetX.map((d: any) => d.value));
  const xRange = xMax - xMin;
  const xStep = axisStep;

  const yMin = Math.min(...datasetY.map((d: any) => d.value));
  const yMax = Math.max(...datasetY.map((d: any) => d.value));
  const yRange = yMax - yMin;
  const yStep = axisStep;

  const y2Min = datasetY2.length > 0 ? Math.min(...datasetY2.map((d: any) => d.value)) : 0;
  const y2Max = datasetY2.length > 0 ? Math.max(...datasetY2.map((d: any) => d.value)) : 0;
  const y2Range = y2Max - y2Min;
  const y2Step = datasetY2.length > 0 ? axisStep : 1;

  const xBins: string[] = [];
  const yBins: string[] = [];
  const y2Bins: string[] = [];
  const zValues: number[][] = Array(Math.ceil(yRange / yStep))
    .fill(null)
    .map(() => Array(Math.ceil(xRange / xStep)).fill(null));
  const zValues2: number[][] = Array(Math.ceil(y2Range / y2Step))
    .fill(null)
    .map(() => Array(Math.ceil(xRange / xStep)).fill(null));

  const xBinCount = Math.ceil(xRange / xStep);
  const yBinCount = Math.ceil(yRange / yStep);
  const y2BinCount = Math.ceil(y2Range / y2Step);

  datasetX.forEach((xData: any, i: number) => {
    const xValue = xData.value;

    if (!datasetY[i] || (datasetY2.length > 0 && !datasetY2[i])) {
      return;
    }

    const yValue = datasetY[i]?.value;
    const y2Value = datasetY2[i]?.value;

    const xBin = Math.max(0, Math.min(xBinCount - 1, Math.floor((xValue - xMin) / xStep)));
    const yBin = Math.max(0, Math.min(yBinCount - 1, Math.floor((yValue - yMin) / yStep)));
    const y2Bin = datasetY2.length > 0 
      ? Math.max(0, Math.min(y2BinCount - 1, Math.floor((y2Value - y2Min) / y2Step)))
      : 0;

    if (zValues[yBin] && zValues[yBin][xBin] === null) {
      zValues[yBin][xBin] = 0;
    }
    zValues[yBin][xBin]++;

    if (datasetY2.length > 0 && zValues2[y2Bin]) {
      if (zValues2[y2Bin][xBin] === null) {
        zValues2[y2Bin][xBin] = 0;
      }
      zValues2[y2Bin][xBin]++;
    }
  });

  for (let i = 0; i <= xBinCount; i++) {
    const binValue = xMin + i * xStep;
    xBins.push(binValue.toFixed(2));
  }

  for (let i = 0; i <= yBinCount; i++) {
    const binValue = yMin + i * yStep;
    yBins.push(binValue.toFixed(2));
  }

  if (datasetY2.length > 0) {
    for (let i = 0; i <= y2BinCount; i++) {
      const binValue2 = y2Min + i * y2Step;
      y2Bins.push(binValue2.toFixed(2));
    }
  }

  return { xBins, yBins, y2Bins, zValues, zValues2 };
};

const GridHeatmapChart = (props: any) => {
  const { xBins, yBins, y2Bins, zValues, zValues2 } = groupDataInGrid(
    props.data,
    props.axisStepSize
  );

  const colorscaleY1Value: [number, string][] = [
    [0, props.yAxis?.minColor ?? defaultMinColor],
    [1, props.yAxis?.maxColor ?? defaultMaxColor],
  ];

  const colorscaleY2Value: [number, string][] = [
    [0, props.yAxisSecondary?.minColor ?? defaultMinColor],
    [1, props.yAxisSecondary?.maxColor ?? defaultMaxColor],
  ];

  const layout: Partial<Layout> = {
    title: props.chartTitle ?? "",
    xaxis: {
      title: `${props.xAxis?.title ?? ""}  ${props.data[0].unit}`,
    },
    yaxis: {
      title: `${props.yAxis?.title ?? ""} ${props.data[1].unit}`,
      scaleanchor: "x",
      overlaying: "y2",
      range: [
        props.yAxis.minValue ?? Math.min(...props.data[1].data.map((d: any) => d.value)),
        props.yAxis.maxValue ?? Math.max(...props.data[1].data.map((d: any) => d.value)),
      ],
    },
    ...(!!props.data[2] && !!props.data[2].data.length
      ? {
          yaxis2: {
            title: `${props.yAxisSecondary?.title ?? ""} ${props.data[2]?.unit}`,
            scaleanchor: "x",

            side: "right",
            range: [
              props.yAxisSecondary?.minValue ??
                Math.min(...props.data[2].data.map((d: any) => d.value)),
              props.yAxisSecondary?.maxValue ??
                Math.max(...props.data[2].data.map((d: any) => d.value)),
            ],
          },
        }
      : {}),
    margin: {
      l: 50,
      r: !!props.data[2] && !!props.data[2].data.length ? 80 : 50,
      t: 50,
      b: 50,
    },
    autosize: false,
    width: 550,
    height: 550,
  };

  const heatmaps: Data[] = [
    {
      x: xBins,
      y: yBins,
      z: zValues,
      type: "heatmap",
      colorscale: colorscaleY1Value,
      showscale: true,
      colorbar: {
        x: !!props.data[2] && !!props.data[2].data.length ? 1.15 : 1.05,
      },
    },
  ];

  if (!!props.data[2] && !!props.data[2].data.length) {
    heatmaps.push({
      x: xBins,
      y: y2Bins,
      z: zValues2,
      type: "heatmap",
      yaxis: "y2",
      colorscale: colorscaleY2Value,
      showscale: true,
      colorbar: {
        x: 1.35,
      },
    });
  }

  return (
    <div>
      <Plot
        data={heatmaps}
        layout={layout}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler
      />
    </div>
  );
};

export default GridHeatmapChart;
